import React, { useState } from 'react';
import useEffectOnce from 'react-use/lib/useEffectOnce';
import { useSet } from 'react-use';
import { WorkflowComponentProps } from '../../Workflow/Workflow.meta';
import { ScrollHereOnce } from '../../CommonComponents/ScrollHereOnce';
import { Text } from '../../CommonComponents/OPL/Text/Text';
import { ProfilePageHeader } from '../components/ProfilePageHeader/ProfilePageHeader';
import { WorkflowFooter } from '../../Workflow/components/WorkflowFooter/WorkflowFooter';
import { Button } from '../../CommonComponents/OPL/Button/Button';
import { useStockOptionsQuery } from './api/useStockOptionsQuery';
import { ProfileTablesLoading } from '../components/ProfileTablesLoading/ProfileTablesLoading';
import { StockOptionsTable } from './components/StockOptionsTable/StockOptionsTable';
import { TableActions } from './components/TableActions/TableActions';
import { formatCurrency } from '../../CommonUtils/formatNumber';
import { Box } from '../../CommonComponents/OPL/Box/Box';
import { ErrorState } from '../../CommonComponents/OPL/AsyncStates/ErrorState/ErrorState';
import { TableRow } from './components/TableRow/TableRow';
import { Icon } from '../../CommonComponents/OPL/Icon/Icon';
import { VestingListTable } from './components/VestingListTable/VestingListTable';
import { GrantToDelete, GrantToUpsert } from './StockOptions.meta';
import { DeleteStockGrantModal } from './components/DeleteStockGrantModal/DeleteStockGrantModal';
import { StockOptionsEmpty } from './StockOptions.empty';
import { Wrapper } from '../components/Wrapper/Wrapper';
import { useResetModuleWrapperStyles } from '../../_hooks/useResetModuleWrapperStyles';
import { UpsertGrantModal } from './components/UpsertGrantModal/UpsertGrantModal';
import { StockOptionsProvider } from './context/StockOptionsProvider';
import { AccountDTO } from '../../Profile/Accounts/api/useAccountsQuery';
import { EditAccountModal } from './components/EditAccountModal/EditAccountModal';

interface Props {
  clientId: string;
  workflowProps?: WorkflowComponentProps;
}

export const StockOptions = ({ clientId, workflowProps }: Props) => {
  useResetModuleWrapperStyles({ isActive: !workflowProps });

  const [grantToUpsert, setGrantToUpsert] = useState<GrantToUpsert | null>(null);

  useEffectOnce(() => {
    const helpKey = ADV.util.setHelp(ADV.HelpDescriptors.StockOptionsRSUs);

    return () => {
      ADV.util.unsetHelp(helpKey);
    };
  });

  return (
    <>
      <ScrollHereOnce options={false} />

      <Wrapper isWorkflow={!!workflowProps}>
        <ProfilePageHeader
          title="Stock Options / Restricted Stock"
          description="Please include any stock options or restricted stock awards from your equity compensation package."
          tooltip="Equity compensation is non-cash pay that represents partial ownership in a company. This type of compensation can take many forms, including options, restricted stock, and performance shares. Equity compensation allows employees of the company to share in profits and encourage retention, particularly when there are vesting requirements. A vesting schedule determines when stock options can be exercised or when restrictions are lifted on restricted stock."
        >
          <Button
            leftIcon="fas fa-plus"
            onClick={() =>
              setGrantToUpsert({
                grant: {},
                account: {},
              })
            }
          >
            Add Stock Grant
          </Button>
        </ProfilePageHeader>

        <StockOptionsContent
          clientId={clientId}
          workflowProps={workflowProps}
          grantToUpsert={grantToUpsert}
          setGrantToUpsert={setGrantToUpsert}
        />
      </Wrapper>

      {workflowProps && (
        <WorkflowFooter
          isFirstStep={workflowProps.isFirstStep}
          onPrevious={workflowProps.onPrevious}
          onNext={workflowProps.onNext}
        />
      )}
    </>
  );
};

interface ContentProps {
  clientId: string;
  workflowProps?: WorkflowComponentProps;
  grantToUpsert: GrantToUpsert | null;
  setGrantToUpsert(grant: GrantToUpsert | null): void;
}

const StockOptionsContent = ({
  clientId,
  grantToUpsert,
  setGrantToUpsert,
  workflowProps,
}: ContentProps) => {
  const [, { has: isGrantRowExpanded, toggle: toggleGrantRowExpanded }] = useSet<string | null>();

  const [grantToDelete, setGrantToDelete] = useState<GrantToDelete | null>(null);

  const [accountToEdit, setAccountToEdit] = useState<AccountDTO | null>(null);

  const { isLoading, isError, refetch, data } = useStockOptionsQuery({ clientId });

  if (isLoading) {
    return <ProfileTablesLoading isWorkflow={!!workflowProps} />;
  }

  if (isError) {
    return <ErrorState onAction={refetch} mx="auto" my="56" />;
  }

  if (!data) {
    return null;
  }

  return (
    <StockOptionsProvider value={{ options: data.Options }}>
      {data.Items.length === 0 ? (
        <StockOptionsEmpty workflowProps={workflowProps} />
      ) : (
        <Box display="flex" flexDirection="column" css="gap: 24px">
          {data.Items.map(account => (
            <StockOptionsTable
              key={account.AccountId}
              title={account.AccountName}
              workflowProps={workflowProps}
              headerActions={
                <Button
                  variant="subtleInactive"
                  leftIcon="fas fa-pencil-alt"
                  onClick={() => setAccountToEdit(account)}
                  ml="4"
                >
                  Edit Account
                </Button>
              }
              onAddAnotherGrant={() => {
                setGrantToUpsert({
                  grant: {
                    GrantType: account.GrantList[0].GrantType,
                  },
                  account,
                  isAddingNewGrant: true,
                });
              }}
            >
              {account.GrantList.map(grant => {
                const hasVestingList = (grant.VestingList || []).length > 0;
                const isExpanded = isGrantRowExpanded(grant.Id);

                return (
                  <React.Fragment key={grant.Id}>
                    <TableRow
                      variant={hasVestingList ? 'clickable' : 'default'}
                      onClick={hasVestingList ? () => toggleGrantRowExpanded(grant.Id) : undefined}
                      isExpanded={isExpanded}
                    >
                      <td>
                        <Box display="flex" alignItems="center">
                          {hasVestingList && (
                            <Icon
                              className={
                                isExpanded ? 'fas fa-chevron-down' : 'fas fa-chevron-right'
                              }
                              color={isExpanded ? 'primary.600' : 'neutral.600'}
                              mr={isExpanded ? 8 : 12}
                            />
                          )}

                          <Text fontWeight={500} textAlign="left" width="100%">
                            {grant.GrantTypeName}
                          </Text>
                        </Box>
                      </td>

                      <td>
                        <Text fontWeight={500} textAlign="left">
                          {account.AccountOwner}
                        </Text>
                      </td>

                      <td>
                        <Text variant="tabular" fontWeight={500} textAlign="right">
                          {formatCurrency(grant.Value, { mantissa: 2 })}
                        </Text>
                      </td>

                      <TableActions
                        grantName="Stock Grant"
                        onEdit={() => setGrantToUpsert({ grant, account })}
                        onDelete={() => setGrantToDelete({ grant, account })}
                      />
                    </TableRow>

                    {hasVestingList && isExpanded && (
                      <tr>
                        <td colSpan={4} style={{ padding: 0 }}>
                          <VestingListTable grant={grant} />
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </StockOptionsTable>
          ))}
        </Box>
      )}

      {grantToUpsert && (
        <UpsertGrantModal
          clientId={clientId}
          grantToUpsert={grantToUpsert}
          onClose={() => setGrantToUpsert(null)}
        />
      )}

      {grantToDelete && (
        <DeleteStockGrantModal
          {...grantToDelete}
          clientId={clientId}
          onClose={() => setGrantToDelete(null)}
        />
      )}

      {accountToEdit && (
        <EditAccountModal
          clientId={clientId}
          account={accountToEdit}
          onClose={() => setAccountToEdit(null)}
        />
      )}
    </StockOptionsProvider>
  );
};
